// THE DISPATCH DESK (tab-agent).
//
// The Agent tab is not somewhere the operator dwells. The audit log says it is a PIT STOP in a
// loop that starts on Documents and ends on Proposals: 22 launches, 6 aborts, and a median of
// five minutes from a run landing to the first review action — on three runs it was NEGATIVE,
// the operator was accepting proposals while the run was still going.
//
// So the page exists to let a solo operator
//   (a) dispatch the cheapest pass that closes a NAMED gap — not choose a "mode";
//   (b) watch a run in flight file evidence, and stop it when it goes wrong;
//   (c) hold every finished run to account against the job it was given.
//
// The falsification test the old page failed: run #43 (`done`, green, 25 proposals) and run #54
// (`done`, green, 34 proposals) were visually identical, and one of them read ZERO of its
// in-scope documents, never closed its pass, and earned 57 tool rejections — 56 of which the
// transcript viewer rendered as SUCCESSFUL calls, because it badged a failure only on
// `part.status === 'error'` while the tool layer answers a refused write with a 422 and a
// plain-text body. That predicate now lives in src/shared/agentFaults.ts.
//
// Four zones, in this order: the instrument line (loud only when it is broken, and the single
// gate on every dispatch control), the desk (dispatch deck at rest, the live pass in flight),
// the ledger (one run = one sentence + its seal; the diagnostics one layer down, in-page, no
// modal), and the corpus dock (collapsed until it is the zone you are in).
//
// Every mutation still goes through the endpoints that already own it. This page adds no write
// path; `/api/agent-desk` is read-only.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useEngagement } from '../context/EngagementContext.js';
import { useToast } from '../components/Toast.js';
import ConfirmModal from '../components/ConfirmModal.js';
import EmptyState, { ProblemState } from '../components/EmptyState.js';
import { usePoll } from '../hooks/usePoll.js';
import { useT, useLocale } from '../i18n/index.js';
import { tCtx } from '../../shared/i18n.js';
import './AgentPage.de.js';
import './AgentPage.fr.js';
import { ROUTES, useDeepLink, useOpenRoute, useRouteParams } from '../routes.js';
import type { Problem } from '../../shared/problems.js';
import { isPushback, isLookupMiss, refusalOf } from '../../shared/agentFaults.js';
import { parseClosingAccount, accountHead } from '../../shared/closingAccount.js';
import {
  agentApi, type AgentHealth, type RunMode, type ScopeDocument, type TurnActivity, type WorkspaceState,
} from '../api/agent.js';
import ChatActivity from '../components/ChatActivity.js';
import {
  agentDeskApi,
  type DeskProgress, type DeskTranscriptMessage, type DeskTranscriptPart,
  type DispatchCard, type DispatchPlan, type Ledger, type LedgerRun, type RunAudit,
} from '../api/agentDesk.js';
import ProposalsPage from './ProposalsPage.js';

/** The translator's shape, for the plain functions outside components that render text. */
type T = ReturnType<typeof useT>;

/** The desk's cadence. A run is 27 s – 12 m 51 s; five seconds is what "watching" means here. */
const DESK_POLL_MS = 5000;

// ───────────────────────────── small formatters ─────────────────────────────

function parsePgTs(ts: string): number {
  // Postgres timestamptz strings: '2026-08-02 00:45:12.323+00' — normalize for Date.parse
  let s = ts.replace(' ', 'T');
  if (/[+-]\d{2}$/.test(s)) s = `${s}:00`;
  return new Date(s).getTime();
}

function elapsed(startedAt: string | null, finishedAt: string | null, now: number): string {
  if (!startedAt) return '—';
  const start = parsePgTs(startedAt);
  const end = finishedAt ? parsePgTs(finishedAt) : now;
  if (!Number.isFinite(start) || !Number.isFinite(end)) return '—';
  const secs = Math.max(0, Math.round((end - start) / 1000));
  if (secs < 60) return `${secs}s`;
  return `${Math.floor(secs / 60)}m ${String(secs % 60).padStart(2, '0')}s`;
}

const stamp = (iso: string | null): string => (iso ? iso.slice(0, 16).replace('T', ' ') : '—');

const Fig = ({ children }: { children: React.ReactNode }) => <span className="voice-figure">{children}</span>;
const Ref = ({ children }: { children: React.ReactNode }) => <span className="src-ref">{children}</span>;

const short = (v: string | null | undefined, n = 10): string => (v ? v.slice(0, n) : '—');

// ───────────────────────────── the instrument line ─────────────────────────────

/**
 * One line, quiet and neutral when ready — and the ONLY loud thing on the page when vLLM is
 * unreachable. Three always-on green lamps destroyed the isolation of the one that matters
 * (Von Restorff); `agent.workspace_refresh` was used TWICE in the whole audit history and the
 * OpenCode version has never gated anything, so both are demoted: the workspace to a quiet
 * control here, the OpenCode version into each run's provenance footer.
 */
function Instrument({
  health, workspace, live, onRecheck, onRebuild, rebuilding,
}: {
  health: AgentHealth | null;
  workspace: WorkspaceState | null;
  live: boolean;
  onRecheck: () => void;
  onRebuild: () => void;
  rebuilding: boolean;
}) {
  const t = useT();
  const reachable = !!health?.vllm.reachable && !health.vllm.error;

  if (health && !reachable) {
    return (
      <div className="instr ag-instrwrap"><div className="instr-band ag-instr ag-instr--broken">
        <span className="ag-dot" style={{ background: 'var(--broken)', marginTop: 5 }} />
        <span className="ag-instr-say">
          <b>{t('The execution plane is down.')}</b>{' '}
          {health.vllm.base_url ? <>{t('vLLM at')} <Ref>{health.vllm.base_url}</Ref> {t('did not answer.')} </> : null}
          {health.vllm.error ? <>{t('The endpoint said: “{error}”.', { error: health.vllm.error })} </> : null}
          {t('Nothing can be dispatched — every pass below would die at its first turn. The register stays fully usable by hand.')}
        </span>
        <span className="ag-instr-acts">
          <button type="button" className="btn btn-sm" onClick={onRecheck}>{t('Re-check')}</button>
        </span>
      </div></div>
    );
  }

  return (
    <div className="instr ag-instrwrap"><div className="instr-band ag-instr">
      <span className="ag-dot" style={{ background: health ? 'var(--ok)' : 'var(--rule-2)' }} />
      <b>{health ? t('Ready') : t('Checking…')}</b>
      {health ? (
        <>
          <span className="sep">|</span>
          <Ref>{health.vllm.model_id || t('no model reported')}</Ref>
          <span className="sep">|</span>
          {t('workspace')}{' '}
          {workspace?.exists
            ? <>
              <Fig>{workspace.document_files}</Fig> {t('docs at')} <Ref>{short(workspace.commit)}</Ref>
            </>
            : <>{t('built automatically when the first run starts')}</>}
          <span className="sep">|</span>
          {/* Caps are only news while something is running — at rest this read like a fault
              report ("0/2 servers") standing next to a green lamp. */}
          {live
            ? <>
              <Fig>{health.runtime.active_runs}</Fig> {t('of')} <Fig>{health.runtime.max_concurrent}</Fig> {t('slots in use')}
              {' · '}{t('this run stops after')} <Fig>{Math.round(health.runtime.max_wall_clock_ms / 60000)}</Fig> {t('min')}
              {' '}{t('or')} <Fig>{health.runtime.max_turns}</Fig> {t('turns')}
            </>
            : <>{health.runtime.max_concurrent === 2 ? t('both run slots free') : t('{n} run slots free', { n: health.runtime.max_concurrent })}</>}
        </>
      ) : null}
      <span className="ag-instr-acts">
        <button type="button" className="btn btn-sm btn-ghost" disabled={rebuilding} onClick={onRebuild}>
          {rebuilding ? t('Rebuilding…') : t('Rebuild workspace')}
        </button>
      </span>
    </div></div>
  );
}

// ───────────────────────────── zone 1 (idle) — the deck ─────────────────────────────

function unitOf(card: DispatchCard): string {
  return card.covers === 1 ? card.unit : card.unit_plural;
}

function DispatchDeck({
  plan, armed, onArm, vllmOk, liveRuns, note, onNote, onDispatch, dispatching,
}: {
  plan: DispatchPlan;
  armed: string | null;
  onArm: (kind: string) => void;
  vllmOk: boolean;
  liveRuns: number;
  note: string;
  onNote: (v: string) => void;
  onDispatch: (card: DispatchCard) => void;
  dispatching: boolean;
}) {
  const t = useT();
  const capped = liveRuns >= 2;
  return (
    <div className="ag-deck">
      {plan.cards.map((card) => {
        const isArmed = armed === card.kind && vllmOk;
        // THE DEFECT THIS FIXES: `New extraction run` carried no `disabled` at all and the three
        // pass cards were gated on readiness and the slot cap but NOT on vllmOk, so with the
        // model down a click started a run that died at its first turn (RESEARCH §4.4).
        const blocked = !vllmOk
          ? t('vLLM is unreachable — the run would die at its first turn.')
          : capped
            ? t('Two runs are already live.')
            : card.blocked_reason;
        return (
          <div
            key={card.kind}
            className={`ag-card${isArmed ? ' ag-card--armed' : ''}${blocked ? ' ag-card--blocked' : ''}`}
            role="button"
            tabIndex={0}
            aria-pressed={isArmed}
            onClick={() => onArm(card.kind)}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onArm(card.kind); } }}
          >
            <div className="ag-card-top">
              <span className="ag-fig voice-figure">{card.covers}</span>
              <span className="ag-unit">
                {unitOf(card)}
                {card.kind === 'new_evidence'
                  ? (card.since_run ? <> {t('since')} <Ref>#{card.since_run}</Ref></> : ` ${t('linked')}`)
                  : ` ${t('uncovered')}`}
              </span>
              {isArmed ? <span className="ag-arm">{t('armed')}</span> : null}
            </div>
            <div className="ag-card-label">{card.label}</div>
            <div className="ag-card-subj">
              {card.subject.length
                ? <>{card.subject.join(', ')}{card.subject_more ? ` ${t('+{n} more', { n: card.subject_more })}` : ''}</>
                : <span className="n-none">{t('no subject')}</span>}
            </div>
            <div className="ag-card-blurb">{card.blurb}</div>
            <div className="ag-card-foot" onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
              {isArmed && !blocked ? (
                <input
                  className="ag-note"
                  placeholder={t('Operator note for this pass — it travels into the prompt')}
                  value={note}
                  onChange={(e) => onNote(e.target.value)}
                />
              ) : null}
              <button
                type="button"
                className={`btn btn-sm${isArmed && !blocked ? ' btn-accent' : ''}`}
                disabled={!!blocked || !card.ready || dispatching}
                title={blocked ?? undefined}
                onClick={() => onDispatch(card)}
              >
                {t('Dispatch')}
              </button>
              {blocked ? <span className={`ag-why${!vllmOk ? ' ag-why--bad' : ''}`}>{blocked}</span> : null}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/**
 * The escape hatch the deck replaced as a DEFAULT but never removed.
 *
 * Today's launcher pre-selects `!covered && !no_text_layer` — on URSC that is 24 of 28
 * documents, a reasonable-looking option that gets satisficed (Krug p.39), so the run the
 * operator wanted over one new document became a 25-document re-sweep costing four minutes of
 * a shared GPU. The selection survives here as a CHOICE, and the free-text instruction that
 * turned a batch run into a targeted one survives with it.
 */
function ScopePicker({
  docs, selected, setSelected, instruction, setInstruction, onStart, starting, blocked, onClose,
}: {
  docs: ScopeDocument[];
  selected: Set<number>;
  setSelected: (next: Set<number>) => void;
  instruction: string;
  setInstruction: (v: string) => void;
  onStart: () => void;
  starting: boolean;
  blocked: string | null;
  onClose: () => void;
}) {
  const t = useT();
  const { fmtDate } = useLocale();
  const uncovered = docs.filter((d) => !d.covered && !d.no_text_layer);
  const toggle = (id: number) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };
  return (
    <div className="ag-picker">
      <div className="ag-picker-head">
        <span className="u-label">{t('Choose documents')}</span>
        <span className="ag-meta">
          <Fig>{selected.size}</Fig> {t('of')} <Fig>{docs.length}</Fig> {t('selected')}
        </span>
        <button type="button" className="btn btn-sm" onClick={() => setSelected(new Set(docs.map((d) => d.id)))}>{t('All')}</button>
        <button type="button" className="btn btn-sm" onClick={() => setSelected(new Set(uncovered.map((d) => d.id)))}>
          {t('Not yet covered ({n})', { n: uncovered.length })}
        </button>
        <button type="button" className="btn btn-sm" onClick={() => setSelected(new Set())}>{t('None')}</button>
        <button type="button" className="btn btn-sm btn-ghost" style={{ marginLeft: 'auto' }} onClick={onClose}>{t('Close')}</button>
      </div>

      <div className="ag-picker-body">
        {docs.length === 0 ? (
          <div className="ag-acct-none">
            {t('No document is linked to this engagement, so a batch pass would have nothing to read.')}
          </div>
        ) : docs.map((d) => (
          <button key={d.id} type="button" className="ag-drow" onClick={() => toggle(d.id)}>
            <input type="checkbox" readOnly checked={selected.has(d.id)} tabIndex={-1} />
            <span className="src-ref">{d.ref}</span>
            <span className="ag-dfile" title={d.original_filename}>{d.original_filename}</span>
            <span className="ag-dwhen voice-figure ag-meta">{fmtDate(d.effective_date)}</span>
            <span className="ag-dstate">
              {d.no_text_layer ? t('no text layer') : d.covered ? t('covered') : t('not yet covered')}
            </span>
          </button>
        ))}
      </div>

      <div className="ag-picker-foot">
        <input
          className="ag-input"
          placeholder={t('Optional instruction — with one, this runs as a targeted pass instead of a batch pass')}
          value={instruction}
          onChange={(e) => setInstruction(e.target.value)}
        />
        <button
          type="button"
          className="btn btn-sm btn-accent"
          disabled={!!blocked || starting}
          title={blocked ?? undefined}
          onClick={onStart}
        >
          {starting ? t('Starting…') : instruction.trim() ? t('Start targeted run') : t('Start batch run ({n} docs)', { n: selected.size || docs.length })}
        </button>
        {blocked ? <span className="ag-why ag-why--bad">{blocked}</span> : null}
      </div>
    </div>
  );
}

// ───────────────────────────── zone 1 (live) — the pass in flight ─────────────────────────────

/**
 * 0 clicks to watch, 1 to nudge, 1 to stop.
 *
 * The live ledger existed and was rendered ONLY inside the slide-in: to nudge you had to
 * notice, click the row, scroll inside the panel, type, and press Nudge — while the
 * conversation panel two zones down had `Stop` inline in its composer, so one product answered
 * one question two ways. Nothing here is new machinery; it is a relocation.
 *
 * The denominator is honest (Doherty: a progress bar need not be accurate, but it must not
 * lie): `N of M documents in scope accounted for`, plus the RULE that ends the wait.
 */
function LivePass({
  run, progress, now, onStop, onNudge,
}: {
  run: LedgerRun;
  progress: DeskProgress | null;
  now: number;
  onStop: () => void;
  onNudge: (message: string) => Promise<void>;
}) {
  const t = useT();
  const { tp } = useLocale();
  const [nudge, setNudge] = useState('');
  const [busy, setBusy] = useState(false);

  const send = async () => {
    const message = nudge.trim();
    if (!message) return;
    setBusy(true);
    try { await onNudge(message); setNudge(''); } finally { setBusy(false); }
  };

  const docs = progress?.docs ?? [];
  const accounted = docs.filter((d) => d.proposals + d.questions > 0).length;
  const discipline = progress?.discipline ?? run.discipline;

  return (
    <div className="ag-live">
      <div className="ag-live-head">
        <span className="ag-live-title">{run.pass_label}</span>
        <Ref>#{run.id}</Ref>
        <span className="ag-meta">
          {t('running')} <Fig>{elapsed(run.started_at ?? run.created_at, null, now)}</Fig>
          {' · '}<Fig>{progress?.turns ?? run.turns}</Fig> {t('turns')}
          {' · '}<Fig>{progress?.tool_calls ?? run.tool_calls}</Fig> {t('tool calls')}
        </span>
        <button type="button" className="btn btn-sm btn-danger" style={{ marginLeft: 'auto' }} onClick={onStop}>
          {t('Stop')}
        </button>
      </div>

      {docs.length ? (
        <>
          <div className="ag-meta">
            <Fig>{accounted}</Fig> {t('of')} <Fig>{docs.length}</Fig> {t('documents in scope accounted for.')}
            {discipline
              ? <> {t('A run ends only when')} <Ref>close_pass</Ref> {t('accepts an account naming every one of them.')}</>
              : <> {t('This run carries no close discipline, so it ends on its own prose.')}</>}
          </div>
          <div className="ag-gg">
            {docs.map((d) => {
              const n = d.proposals + d.questions;
              return (
                <span
                  key={d.ref}
                  className={`ag-cell${n ? ' ag-cell--done' : ''}`}
                  title={n
                    ? t('{proposals}, {questions} from {ref}', {
                      proposals: tp(d.proposals, '{n} proposal', '{n} proposals'),
                      questions: tp(d.questions, '{n} question', '{n} questions'),
                      ref: d.ref,
                    })
                    : t('nothing filed from {ref} yet', { ref: d.ref })}
                >
                  {d.ref}{n ? ` ·${n}` : ''}
                </span>
              );
            })}
          </div>
        </>
      ) : (
        <div className="ag-meta">
          {t('This pass walks the register rather than a document set, so there is no per-document tally — watch what it files.')}
        </div>
      )}

      <div style={{ marginTop: 14 }}>
        <div className="u-label" style={{ marginBottom: 6 }}>
          {t('Filed so far — {proposals}, {questions}', {
            proposals: tp(progress?.totals.proposals ?? 0, '{n} proposal', '{n} proposals'),
            questions: tp(progress?.totals.questions ?? 0, '{n} question', '{n} questions'),
          })}
        </div>
        {progress && progress.recent.length ? progress.recent.map((r, i) => (
          <div key={`${r.at}-${i}`} className="ag-filed">
            <span className="ag-pill att--proposed">{r.kind.replace('create_', '')}</span>
            <span className="ev" style={{ border: 0, padding: 0, margin: 0, flex: 1, minWidth: 0 }}>
              <span className="ev-q" style={{ fontSize: 'var(--t-scan)', lineHeight: 1.5 }}>{r.text}</span>
              <span className="ev-src">{r.document_ref ?? t('no document cited')}</span>
            </span>
          </div>
        )) : (
          <div className="ag-meta">{t('Nothing filed yet. Statements appear here the moment they reach the database.')}</div>
        )}
      </div>

      <div className="ag-row-in">
        <input
          className="ag-input"
          placeholder={t('Nudge — the agent reads this at its next turn boundary')}
          value={nudge}
          onChange={(e) => setNudge(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && nudge.trim() && !busy) void send(); }}
        />
        <button type="button" className="btn btn-sm" disabled={!nudge.trim() || busy} onClick={() => void send()}>
          {t('Nudge')}
        </button>
      </div>
    </div>
  );
}

// ───────────────────────────── zone 2 — the ledger ─────────────────────────────

/** One run = one sentence. Output (turns, tools, tokens) is demoted to the provenance footer. */
function verdictOf(r: LedgerRun, t: T): React.ReactNode {
  const parts: React.ReactNode[] = [];
  if (r.discipline === 'docs' || r.mode === 'batch') {
    parts.push(<>{t('scope')} <span className="fig">{r.scope_n}</span> {r.scope_n === 1 ? t('document') : t('documents')} · {t('read')} <span className="fig">{r.docs_read}</span></>);
  } else if (r.discipline === 'register' || r.discipline === 'register_acs') {
    parts.push(<>{t('walked the register')}</>);
  } else if (r.discipline === 'fact_namespaces') {
    parts.push(<>{t('read')} <span className="fig">{r.docs_read}</span> {t('of')} <span className="fig">{r.scope_n}</span> {t('in scope')}</>);
  } else if (r.mode === 'conversation') {
    parts.push(<><span className="fig">{r.turns}</span> {t('turns with you')}</>);
  } else {
    parts.push(<>{t('no close discipline')}</>);
  }

  if (r.claimed > r.filed) {
    parts.push(<>{t('claimed')} <span className="fig">{r.claimed}</span> {t('filed,')} <span className="fig">{r.filed}</span> {t('on record')}</>);
  } else if (r.filed) {
    const bits: React.ReactNode[] = [];
    if (r.held) bits.push(<><span className="fig">{r.held}</span> {t('held')}</>);
    if (r.rejected) bits.push(<><span className="fig">{r.rejected}</span> {t('rejected')}</>);
    if (r.pending) bits.push(<><span className="fig">{r.pending}</span> {t('waiting on you')}</>);
    parts.push(
      <>{t('filed')} <span className="fig">{r.filed}</span>
        {bits.length ? <> → {bits.map((b, i) => <span key={i}>{i ? ', ' : ''}{b}</span>)}</> : null}
      </>,
    );
  } else if (r.facts || r.events) {
    parts.push(
      <>{t('recorded')} <span className="fig">{r.facts}</span> {r.facts === 1 ? t('fact') : t('facts')},{' '}
        <span className="fig">{r.events}</span> {r.events === 1 ? t('event') : t('events')}</>,
    );
  } else {
    parts.push(<>{t('filed')} <span className="n-none">—</span></>);
  }

  return parts.map((p, i) => <span key={i}>{i ? ' · ' : ''}{p}</span>);
}

function Seal({ seal }: { seal: LedgerRun['seal'] }) {
  if (!seal) return null;
  return <span className={`ag-seal ag-seal--${seal.tone}`}>{seal.label}</span>;
}

// ───────────────────────────── the run expansion ─────────────────────────────

function ClosingAccount({ audit }: { audit: RunAudit }) {
  const t = useT();
  const parsed = useMemo(() => parseClosingAccount(audit.closing_account), [audit.closing_account]);
  const [full, setFull] = useState(false);
  const HEAD = 9;
  const shown = full ? parsed.lines : parsed.lines.slice(0, HEAD);

  return (
    <div className="ag-sub">
      <div className="ag-sub-head">
        <span className="u-label">{t('The account it closed on')}</span>
        <span className="ag-meta">{accountHead(parsed, audit.discipline)}</span>
      </div>
      {!parsed.total ? (
        <div className="ag-acct">
          <div className="ag-acct-none">
            <b>{t('No closing account.')}</b>{' '}
            {audit.discipline
              ? t('This pass carried a close discipline and never satisfied it, so there is no statement of what it did — only the transcript.')
              : t('A conversation and a plain targeted run end on their own prose; there is nothing to validate.')}
          </div>
        </div>
      ) : (
        <>
          <div className="ag-acct">
            {shown.map((line, i) => {
              const openSection = i === 0 || shown[i - 1]?.section !== line.section;
              return (
                <div key={`${line.key}-${i}`}>
                  {line.section && openSection
                    ? <div className="ag-acct-sec u-label">{line.section}</div>
                    : null}
                  <div className={`ag-acct-row${line.muted ? ' ag-acct-row--zero' : ''}`}>
                    <span className="ag-acct-k">{line.key}</span>
                    <span>{line.text}</span>
                    <span className="ag-acct-n voice-figure">{line.tally === null ? '' : line.tally}</span>
                  </div>
                </div>
              );
            })}
            {/* Krug's candour test, applied to the system's own guardrail. */}
            {parsed.warning ? <div className="ag-acct-warn">{parsed.warning}</div> : null}
          </div>
          {parsed.lines.length > HEAD ? (
            <div className="ag-meta" style={{ marginTop: 6 }}>
              {full
                ? t('Showing the whole account.')
                : t('Showing the head of the account — {shown} of {total} lines.', { shown: shown.length, total: parsed.lines.length })}{' '}
              <button type="button" className="ag-link" onClick={() => setFull((v) => !v)}>
                {full ? t('Show the head') : t('Full account')}
              </button>
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}

const MARK: Record<string, string> = {
  accepted: 'att--held',
  accepted_with_edits: 'att--held',
  pending: 'att--proposed',
};

function statusSentence(p: RunAudit['proposals'][number], t: T): { text: string; bad: boolean } {
  const note = p.reviewer_note ? ` · ${p.reviewer_note}` : '';
  if (p.status === 'pending') return { text: t('pending review'), bad: false };
  if (p.status === 'rejected') {
    return {
      text: `${t('rejected — {reason}', { reason: p.reject_reason ?? t('no reason recorded') })}${note}`,
      bad: true,
    };
  }
  if (p.status === 'accepted_with_edits') {
    return { text: `${t('accepted with edits')}${note}`, bad: false };
  }
  if (p.status === 'superseded') return { text: t('superseded'), bad: false };
  return { text: `${tCtx(t, 'accepted', 'proposal')}${note}`, bad: false };
}

/** LAW 1: a resolved citation is `.ev` + `.ev-src`, never a `<pre>` of JSON. */
function FiledStatements({ audit }: { audit: RunAudit }) {
  const t = useT();
  if (!audit.proposals.length) return null;
  return (
    <div className="ag-sub">
      <div className="ag-sub-head">
        <span className="u-label">{t('What it filed')}</span>
        <span className="ag-meta">{t('Every statement carries the span it was taken from.')}</span>
      </div>
      {audit.proposals.map((p) => {
        const payload = p.payload ?? {};
        const st = statusSentence(p, t);
        // A conflict has no `statement`: its claim is the TOPIC, and its substance is the two
        // positions it holds apart. Rendering only `statement` made every conflict on this
        // engagement read "(no text recorded)".
        const statement = String(
          payload.statement ?? payload.name ?? payload.title ?? payload.question ?? payload.topic ?? t('(no text recorded)'),
        );
        const positions = [payload.position_a, payload.position_b]
          .filter((x): x is Record<string, unknown> => !!x && typeof x === 'object')
          .map((x) => ({ statement: String(x.statement ?? ''), ref: String(x.document_ref ?? '') }))
          .filter((x) => x.statement);
        const dod = payload.definition_of_done ? String(payload.definition_of_done) : null;
        const refs = Array.isArray(payload.satisfies_refs) ? payload.satisfies_refs.map(String)
          : Array.isArray(payload.ac_refs) ? payload.ac_refs.map(String)
            : payload.requirement_ref ? [String(payload.requirement_ref)] : [];
        return (
          <div key={p.id} className="ag-prop">
            <div className="ag-prop-head">
              {/* ATTEST §5: ◇ on what is still proposed, ▪ on what was held, no mark on a
                  rejection — a rejected statement makes no claim at all. */}
              <span className={MARK[p.status] ?? ''}>{p.kind.replace('create_', '')}</span>
              <Ref>{t('proposal #{id}', { id: p.id })}</Ref>
              <span className={`ag-prop-st${st.bad ? ' ag-prop-st--bad' : ''}`}>{st.text}</span>
            </div>
            <div className="voice-record">{statement}</div>
            {positions.map((pos, i) => (
              <div className="ag-meta" key={i} style={{ marginTop: 4 }}>
                {pos.ref ? <><Ref>{pos.ref}</Ref>{' — '}</> : null}{pos.statement}
              </div>
            ))}
            {dod ? (
              <>
                <div className="ag-meta" style={{ marginTop: 4 }}>{t('Definition of done')}</div>
                <div className="voice-record" style={{ fontSize: 'var(--t-scan)' }}>{dod}</div>
              </>
            ) : null}
            {(p.evidence ?? []).map((ev, i) => (
              ev?.quoted_span ? (
                <div className="ev" key={i}>
                  <div className="ev-q">{ev.quoted_span}</div>
                  <span className="ev-src">{ev.document_ref ?? t('document #{id}', { id: ev.document_id ?? '?' })}</span>
                </div>
              ) : null
            ))}
            {refs.length ? (
              <div className="ag-reflist">
                {refs.map((r) => <span key={r} className="ag-pill">{r}</span>)}
              </div>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

function argOf(part: DeskTranscriptPart): string {
  const input = part.input;
  if (input === null || input === undefined) return '';
  const raw = typeof input === 'string' ? input : JSON.stringify(input);
  return raw.length > 160 ? `${raw.slice(0, 160)}…` : raw;
}

/** The transcript, with the pushbacks dressed as pushbacks at last. */
function Transcript({ messages }: { messages: DeskTranscriptMessage[] }) {
  const t = useT();
  const promptTurn = messages.findIndex((m) => m.role === 'user');
  if (!messages.length) {
    return (
      <div className="ag-sub">
        <div className="ag-sub-head"><span className="u-label">{t('Transcript')}</span></div>
        <div className="ag-tsheet"><div className="ag-acct-none">{t('No transcript was captured for this run.')}</div></div>
      </div>
    );
  }
  return (
    <div className="ag-sub">
      <div className="ag-sub-head">
        <span className="u-label">{t('Transcript — the provenance record')}</span>
        <span className="ag-meta">{t('Every pushback the tool layer gave, in the words it gave them.')}</span>
      </div>
      <div className="ag-tsheet">
        {messages.map((m, mi) => {
          const texts = m.parts.filter((p) => p.type === 'text' && (p.text ?? '').trim());
          const tools = m.parts.filter((p) => p.type === 'tool');
          if (!texts.length && !tools.length) return null;
          // The FIRST user turn is the pass prompt itself — four kilobytes of scaffolding and a
          // document roster. It is shown in full under Provenance, so here it is named, not
          // dumped: a wall of prompt at the head of the transcript buries the run's own words.
          const isUser = m.role === 'user';
          const firstUser = mi === promptTurn;
          if (firstUser) {
            return (
              <div className="ag-tstep" key={m.id ?? mi}>
                <p className="ag-tsay ag-tuser">{t('The pass prompt it was given — in full under Provenance, below.')}</p>
              </div>
            );
          }
          return (
            <div className="ag-tstep" key={m.id ?? mi}>
              {isUser ? <div className="u-label" style={{ marginBottom: 4 }}>{t('You / the continuation')}</div> : null}
              {texts.map((p, i) => <p className={`ag-tsay${isUser ? ' ag-tuser' : ''}`} key={i}>{p.text}</p>)}
              {tools.map((p, i) => {
                const push = isPushback(p);
                const miss = !push && isLookupMiss(p);
                const refusal = (push || miss) ? refusalOf(p) : null;
                return (
                  <div className={`ag-tcall${push ? ' push' : ''}`} key={i}>
                    <span className={`ag-pill${push ? ' ag-pill--push' : miss ? ' ag-pill--miss' : ''}`}>
                      {push ? t('pushed back') : miss ? t('missed') : t('ok')}
                    </span>
                    <span className="ag-tname">{p.tool}</span>
                    <span className="ag-targ" title={argOf(p)}>{argOf(p)}</span>
                    {refusal ? (
                      <span className={push ? 'ag-push-msg' : 'ag-miss-msg'}>{refusal}</span>
                    ) : null}
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function RunExpansion({
  runId, run, onDelete, onOpenThread, onRetry,
}: {
  runId: number;
  run: LedgerRun;
  onDelete: () => void;
  onOpenThread: () => void;
  onRetry: () => void;
}) {
  const t = useT();
  const { fmtNum } = useLocale();
  const [audit, setAudit] = useState<RunAudit | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showPrompt, setShowPrompt] = useState(false);

  const load = useCallback(async () => {
    setError(null);
    try { setAudit(await agentDeskApi.run(runId)); } catch (err) {
      setError(err instanceof Error ? err.message : t('Could not read the run'));
    }
  }, [runId, t]);

  useEffect(() => { void load(); }, [load]);

  if (error) {
    return (
      <div className="ag-open">
        <EmptyState
          kind="error"
          title={t('This run could not be read')}
          detail={error}
          onRetry={() => { void load(); onRetry(); }}
        />
      </div>
    );
  }
  if (!audit) return <div className="ag-open"><div className="ag-meta" style={{ padding: '12px 0' }}>{t('Reading the run…')}</div></div>;

  const p = audit.provenance;
  return (
    <div className="ag-open">
      <ClosingAccount audit={audit} />
      <FiledStatements audit={audit} />
      <Transcript messages={audit.transcript} />

      <div className="ag-sub">
        <div className="ag-sub-head"><span className="u-label">{t('Provenance')}</span></div>
        <div className="ag-prov">
          {t('model')} <Ref>{p.model_id ?? '—'}</Ref>
          {' · '}{t('workspace')} <Ref>{short(p.workspace_commit, 8)}</Ref>
          {' · '}{t('skills')} <Ref>{short(p.skill_commit, 8)}</Ref>
          {' · '}{t('config')} <Ref>{short(p.config_hash, 16)}</Ref>
          {' · '}<Fig>{p.turns}</Fig> {t('turns')}, <Fig>{p.tool_calls}</Fig> {t('tool calls')},{' '}
          <Fig>{fmtNum(p.tokens_in)}</Fig> {t('tokens in')}, <Fig>{fmtNum(p.tokens_out)}</Fig> {t('out')}
          {p.lookup_misses ? (
            <>{' · '}<Fig>{p.lookup_misses}</Fig> {t('lookup misses')} — <em>{t('ordinary searching, not pushbacks')}</em></>
          ) : null}
          {audit.documents_in_scope.length ? (
            <div style={{ marginTop: 6 }}>
              {t('scope:')}{' '}
              {audit.documents_in_scope.map((d, i) => (
                <span key={d.id}>{i ? ', ' : ''}<Ref>{d.ref}</Ref></span>
              ))}
            </div>
          ) : null}
          <div className="ag-prov-acts">
            {audit.prompt ? (
              <button type="button" className="btn btn-sm btn-ghost" onClick={() => setShowPrompt((v) => !v)}>
                {showPrompt ? t('Hide the prompt it was given') : t('The prompt it was given')}
              </button>
            ) : null}
            {run.mode === 'conversation' ? (
              <button type="button" className="btn btn-sm" onClick={onOpenThread}>{t('Open this thread')}</button>
            ) : null}
            <button type="button" className="btn btn-sm" onClick={onDelete}>{t('Delete run')}</button>
          </div>
          {showPrompt && audit.prompt ? (
            <pre className="mono" style={{ whiteSpace: 'pre-wrap', margin: '10px 0 0', fontSize: 12, color: 'var(--ink-2)' }}>
              {audit.prompt}
            </pre>
          ) : null}
        </div>
      </div>
    </div>
  );
}

// ───────────────────────────── zone 3 — the corpus dock ─────────────────────────────

interface ChatEntry {
  role: 'user' | 'assistant';
  text: string;
  tools?: { tool: string; pushed: boolean }[];
  refs?: { id: number; label: string }[];
}

/**
 * Rebuild the chat log from a stored run transcript. The transcript is a sequence of model
 * turns, most of them tool calls with no prose; one bubble per turn produced a column of empty
 * bubbles labelled DOCUMENT_GET_SPAN with the answer buried at the bottom. A chat has two
 * voices, so tool activity is accumulated and attached to the answer it produced.
 */
function chatFromTranscript(messages: DeskTranscriptMessage[]): ChatEntry[] {
  const out: ChatEntry[] = [];
  let pending: { tool: string; pushed: boolean }[] = [];

  for (const m of messages) {
    const text = m.parts.filter((p) => p.type === 'text' && (p.text ?? '').trim())
      .map((p) => String(p.text).trim()).join('\n\n');
    const tools = m.parts.filter((p) => p.type === 'tool')
      .map((p) => ({ tool: String(p.tool ?? ''), pushed: isPushback(p) }));

    if (m.role === 'user') {
      // A first turn carries the run scaffolding ahead of the question. Show the question.
      const marker = '\nOperator:\n';
      const own = text.includes(marker) ? text.slice(text.lastIndexOf(marker) + marker.length) : text;
      if (own.trim()) out.push({ role: 'user', text: own.trim() });
      continue;
    }
    pending = pending.concat(tools);
    if (!text) continue;
    out.push({ role: 'assistant', text, tools: pending.length ? pending : undefined });
    pending = [];
  }
  if (pending.length) out.push({ role: 'assistant', text: '', tools: pending });
  return out;
}

// ───────────────────────────── the page ─────────────────────────────

interface Desk {
  engagementId: number | null;
  health: AgentHealth | null;
  workspace: WorkspaceState | null;
  dispatch: DispatchPlan | null;
  ledger: Ledger | null;
  docs: ScopeDocument[];
  progress: DeskProgress | null;
  problems: Problem[];
}

const emptyDesk = (engagementId: number | null): Desk => ({
  engagementId, health: null, workspace: null, dispatch: null, ledger: null,
  docs: [], progress: null, problems: [],
});

export default function AgentPage() {
  const t = useT();
  const { tp } = useLocale();
  const nav = useEngagement();
  const { current } = nav;
  const openRoute = useOpenRoute(nav);
  const routeParams = useRouteParams();
  const toast = useToast();
  const engagementId = current?.id ?? null;

  // ── the one poll ────────────────────────────────────────────────────────
  // usePoll's law: numbers are NEVER blanked because a refresh failed. A composite of five
  // reads has to honour that per SOURCE, so the last good value of each one is held here and
  // only the failing source degrades — with its producer's own message (B7).
  const held = useRef<Desk>(emptyDesk(engagementId));

  const load = useCallback(async (): Promise<Desk> => {
    if (!engagementId) return emptyDesk(null);
    if (held.current.engagementId !== engagementId) held.current = emptyDesk(engagementId);
    const keep = held.current;
    const problems: Problem[] = [];

    const [h, w, d, docs] = await Promise.allSettled([
      agentApi.health(),
      agentApi.workspace(engagementId),
      agentDeskApi.dispatch(engagementId),
      agentApi.documents(engagementId),
    ]);
    if (h.status === 'fulfilled') keep.health = h.value;
    else problems.push({ source: 'health', message: String(h.reason?.message ?? h.reason) });
    if (w.status === 'fulfilled') keep.workspace = w.value;
    else problems.push({ source: 'workspace', message: String(w.reason?.message ?? w.reason) });
    if (d.status === 'fulfilled') { keep.dispatch = d.value; problems.push(...d.value.problems); }
    else problems.push({ source: 'dispatch', message: String(d.reason?.message ?? d.reason) });
    if (docs.status === 'fulfilled') keep.docs = docs.value.documents;
    else problems.push({ source: 'documents', message: String(docs.reason?.message ?? docs.reason) });

    try {
      const ledger = await agentDeskApi.ledger(engagementId);
      keep.ledger = ledger;
      problems.push(...ledger.problems);
    } catch (err) {
      problems.push({ source: 'ledger', message: err instanceof Error ? err.message : String(err) });
    }

    const liveRun = keep.ledger?.runs.find((r) => r.live && r.mode !== 'conversation') ?? null;
    if (liveRun) {
      try { keep.progress = await agentDeskApi.progress(liveRun.id); } catch { /* the cells keep their last good state */ }
    } else {
      keep.progress = null;
    }

    return { ...keep, problems };
  }, [engagementId]);

  const { data, loading, error, notice, refresh } = usePoll(load, DESK_POLL_MS);

  // The live elapsed clock ticks independently of the poll: a run's age must not freeze for
  // five seconds at a time.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  // ── page-local state ────────────────────────────────────────────────────
  /**
   * The page's two faces: the dispatch DESK (everything below), and the PROPOSALS queue folded
   * into it as a sub-tab — ProposalsPage renders as a child, prop-free, reading its own deep
   * links (`run`, `status`) from the same RouteParamsContext this page reads `tab` from.
   */
  const [tab, setTab] = useState<'desk' | 'proposals'>('desk');
  const [armed, setArmed] = useState<string | null>(null);
  const [note, setNote] = useState('');
  const [dispatching, setDispatching] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [instruction, setInstruction] = useState('');
  const [starting, setStarting] = useState(false);
  const [rebuilding, setRebuilding] = useState(false);
  const [openRun, setOpenRun] = useState<number | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<LedgerRun | null>(null);

  const [dockOpen, setDockOpen] = useState(false);
  const [chat, setChat] = useState<ChatEntry[]>([]);
  const [chatRunId, setChatRunId] = useState<number | null>(null);
  const [chatInput, setChatInput] = useState('');
  const [chatBusy, setChatBusy] = useState(false);
  /** The live doing behind the blocking turn — clock-only until the run id is known. */
  const [chatAct, setChatAct] = useState<TurnActivity | null>(null);
  const [chatStartedAt, setChatStartedAt] = useState(0);
  useEffect(() => {
    if (!chatBusy || chatRunId === null) { setChatAct(null); return; }
    let live = true;
    const poll = async () => {
      try {
        const a = await agentApi.runActivity(chatRunId);
        if (live) setChatAct(a.busy ? a : null);
      } catch { /* transient — next poll answers */ }
    };
    void poll();
    const iv = setInterval(() => void poll(), 2000);
    return () => { live = false; clearInterval(iv); };
  }, [chatBusy, chatRunId]);
  const chatLog = useRef<HTMLDivElement>(null);
  const dockRef = useRef<HTMLDivElement>(null);

  // Switching engagement resets every page-local intent (B4) — but the FIRST resolution of the
  // engagement is not a switch. On a cold deep link (`#agent?engagement=191&docs=…`) `current`
  // is null until the engagement list lands, and resetting on that transition wiped the arrival
  // intent useDeepLink had already applied.
  const lastEngagement = useRef<number | null>(engagementId);
  useEffect(() => {
    if (lastEngagement.current === engagementId) return;
    const wasResolved = lastEngagement.current !== null;
    lastEngagement.current = engagementId;
    if (!wasResolved) return;
    setArmed(null); setNote(''); setPickerOpen(false); setSelected(new Set());
    setInstruction(''); setOpenRun(null); setDockOpen(false); setChat([]); setChatRunId(null);
  }, [engagementId]);

  // ── the deep link from Documents: `#agent?docs=3,9,12` ───────────────────
  // The programme chose the deep link over a new POST route, so arriving with a document set
  // pre-fills the picker's scope and opens it — an arrival INTENT, one-shot, cleared on arrival.
  useDeepLink(routeParams, {
    docs: (v) => {
      const ids = v.split(',').map(Number).filter((n) => Number.isInteger(n) && n > 0);
      if (!ids.length) return;
      setSelected(new Set(ids));
      setPickerOpen(true);
      setArmed(null);
      setTab('desk');
    },
    // `tab` names the sub-tab outright (ROUTES.proposals emits '#agent?tab=proposals&…').
    // `run` and `status` are ProposalsPage's own intents — it reads their VALUES itself from
    // RouteParamsContext once mounted — but their presence carries the same meaning a
    // `tab=proposals` does: a bare '#proposals?run=…' from an old notification arrives through
    // the registry alias without the tab param, and must still land on the queue.
    tab: (v) => setTab(v === 'proposals' ? 'proposals' : 'desk'),
    run: () => setTab('proposals'),
    status: () => setTab('proposals'),
  });

  const desk = data;
  const health = desk?.health ?? null;
  const vllmOk = !!health?.vllm.reachable && !health.vllm.error;
  const runs = useMemo(() => desk?.ledger?.runs ?? [], [desk]);
  const liveRuns = runs.filter((r) => r.live);
  const livePass = liveRuns.find((r) => r.mode !== 'conversation') ?? null;
  const clearable = runs.filter((r) => !r.live && r.filed === 0 && r.claimed === 0).length;
  const problems = desk?.problems ?? [];

  // ── actions (every one of them an endpoint that already existed) ─────────
  const dispatchCard = async (card: DispatchCard) => {
    if (!engagementId) return;
    setDispatching(true);
    try {
      if (card.kind === 'new_evidence') {
        const run = await agentApi.startRun(engagementId, {
          mode: 'batch',
          prompt: note.trim() || undefined,
          document_ids: card.document_ids,
        });
        toast('ok', tp(card.document_ids.length, 'Run #{id} dispatched over {n} document', 'Run #{id} dispatched over {n} documents', { id: run.id }));
      } else {
        const run = await agentApi.runPass(engagementId, card.kind, true, note.trim() || undefined);
        toast('ok', t('{label} started — proposals land in the review queue', { label: run.label }));
      }
      setNote('');
      setArmed(null);
      await refresh();
    } catch (err) {
      toast('err', err instanceof Error ? err.message : t('Could not dispatch the pass'));
    } finally {
      setDispatching(false);
    }
  };

  const startFromPicker = async () => {
    if (!engagementId) return;
    const mode: RunMode = instruction.trim() ? 'targeted' : 'batch';
    setStarting(true);
    try {
      const run = await agentApi.startRun(engagementId, {
        mode,
        prompt: instruction.trim() || undefined,
        document_ids: [...selected],
      });
      toast('ok', t('Run #{id} queued', { id: run.id }));
      setPickerOpen(false);
      setInstruction('');
      await refresh();
    } catch (err) {
      toast('err', err instanceof Error ? err.message : t('Could not start the run'));
    } finally {
      setStarting(false);
    }
  };

  const stopRun = async (id: number) => {
    try {
      await agentApi.abortRun(id);
      toast('warn', t('Run #{id} stopped', { id }));
      await refresh();
    } catch (err) {
      toast('err', err instanceof Error ? err.message : t('Could not stop the run'));
    }
  };

  const nudgeRun = async (id: number, message: string) => {
    try {
      await agentApi.nudgeRun(id, message);
      toast('ok', t('Nudge queued — the agent reads it at its next turn boundary'));
    } catch (err) {
      toast('err', err instanceof Error ? err.message : t('Nudge failed'));
    }
  };

  const rebuildWorkspace = async () => {
    if (!engagementId) return;
    setRebuilding(true);
    try {
      const result = await agentApi.refreshWorkspace(engagementId);
      toast('ok', t('Workspace committed {sha}', { sha: result.commitSha?.slice(0, 8) ?? '' }));
      await refresh();
    } catch (err) {
      toast('err', err instanceof Error ? err.message : t('Workspace refresh failed'));
    } finally {
      setRebuilding(false);
    }
  };

  const deleteRun = async (run: LedgerRun) => {
    try {
      const r = await agentApi.deleteRun(run.id);
      if (chatRunId === run.id) { setChatRunId(null); setChat([]); }
      if (openRun === run.id) setOpenRun(null);
      toast('ok', r.proposals_kept
        ? tp(r.proposals_kept, 'Run #{id} deleted — {n} proposal kept', 'Run #{id} deleted — {n} proposals kept', { id: run.id })
        : t('Run #{id} deleted', { id: run.id }));
      await refresh();
    } catch (err) {
      toast('err', err instanceof Error ? err.message : t('Could not delete the run'));
    }
  };

  const cleanupRuns = async () => {
    if (!engagementId) return;
    try {
      const r = await agentApi.cleanupRuns(engagementId);
      const cleared = tp(r.deleted, 'Cleared {n} run', 'Cleared {n} runs');
      toast(r.deleted ? 'ok' : 'warn',
        r.deleted
          ? (r.kept_because_they_have_proposals
            ? t('{cleared} — kept {n} that produced proposals', { cleared, n: r.kept_because_they_have_proposals })
            : cleared)
          : t('Nothing to clear — every finished run produced proposals'));
      await refresh();
    } catch (err) {
      toast('err', err instanceof Error ? err.message : t('Cleanup failed'));
    }
  };

  const openThread = useCallback(async (runId: number) => {
    try {
      const audit = await agentDeskApi.run(runId);
      setChatRunId(runId);
      setChat(chatFromTranscript(audit.transcript));
      setDockOpen(true);
      requestAnimationFrame(() => dockRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }));
    } catch (err) {
      toast('err', err instanceof Error ? err.message : t('Could not open that thread'));
    }
  }, [toast, t]);

  const sendChat = async () => {
    if (!engagementId || !chatInput.trim() || chatBusy) return;
    const message = chatInput.trim();
    setChat((prev) => [...prev, { role: 'user', text: message }]);
    setChatInput('');
    setChatBusy(true);
    setChatStartedAt(Date.now());
    try {
      const result = await agentApi.conversation(engagementId, message, chatRunId);
      const runId = Number(result.run.id);
      setChatRunId(runId);
      const audit = await agentDeskApi.run(runId).catch(() => null);
      const lastAssistant = (audit?.transcript ?? []).filter((m) => m.role === 'assistant').slice(-1)[0];
      setChat((prev) => [...prev, {
        role: 'assistant',
        text: result.reply || t('(no text in the reply — open the run below for its transcript)'),
        tools: (lastAssistant?.parts ?? []).filter((p) => p.type === 'tool')
          .map((p) => ({ tool: String(p.tool ?? '?'), pushed: isPushback(p) })),
        refs: [
          ...result.proposals.map((p) => ({ id: runId, label: t('proposal #{id} · {kind}', { id: p.id, kind: p.kind }) })),
          ...result.open_questions.map((q) => ({ id: runId, label: q.ref })),
        ],
      }]);
      await refresh();
    } catch (err) {
      setChat((prev) => [...prev, { role: 'assistant', text: `⚠ ${err instanceof Error ? err.message : t('Failed')}` }]);
    } finally {
      setChatBusy(false);
    }
  };

  const stopChat = async () => {
    if (!chatRunId) return;
    await stopRun(chatRunId);
    setChatBusy(false);
    setChat((prev) => [...prev, { role: 'assistant', text: t('⏹ Stopped by you. The thread is kept — ask again to carry on.') }]);
  };

  // The log is its own scroll container, so drive its scrollTop directly: the window can never
  // move as a side effect (a `scrollIntoView` on an empty thread used to drag the whole page).
  useEffect(() => {
    if (!chat.length && !chatBusy) return;
    const log = chatLog.current;
    if (log) log.scrollTo({ top: log.scrollHeight, behavior: 'smooth' });
  }, [chat, chatBusy]);

  // ── no engagement ───────────────────────────────────────────────────────
  if (!current) {
    return (
      <div className="page ag">
        <header className="mast"><div className="mast-top"><div className="mast-main"><h1>{t('Agent')}</h1><div className="mast-sub">{t('no engagement selected')}</div></div></div></header>
        <div style={{ marginTop: 24 }}>
          <EmptyState
            kind="empty"
            title={t('Extraction is always scoped to one engagement')}
            detail={t("A run reads that engagement's linked documents and files into its register. Pick an engagement in the top bar and its desk loads here.")}
          />
        </div>
      </div>
    );
  }

  const lastThread = runs.find((r) => r.mode === 'conversation') ?? null;
  const standing = desk?.ledger?.standing ?? null;
  /** The proposals tab's badge — claims still standing, summed across the ledger's runs. */
  const pendingTotal = runs.reduce((sum, r) => sum + r.pending, 0);

  return (
    <div className="page ag">
      {/* ══ The page's two faces — the dispatch desk, and the proposals queue it feeds.
             Same .tabs.reg-tabs dress as the register's row tabs. ══ */}
      <div className="tabs reg-tabs ag-tabs">
        <button
          className={`tab ${tab === 'desk' ? 'active' : ''}`}
          aria-selected={tab === 'desk'}
          onClick={() => setTab('desk')}
        >
          {t('Desk')}
        </button>
        <button
          className={`tab ${tab === 'proposals' ? 'active' : ''}`}
          aria-selected={tab === 'proposals'}
          onClick={() => setTab('proposals')}
        >
          {t('Proposals')} {pendingTotal > 0 ? <span className="reg-tabn voice-figure">{pendingTotal}</span> : null}
        </button>
      </div>

      {tab === 'proposals' ? (
        // The queue, whole and prop-free — its own masthead, rail and stand. It reads its own
        // deep links (`run`, `status`) from RouteParamsContext; only the chrome around it moved.
        <ProposalsPage />
      ) : (
        <>
      {/* ══ Zone 0 — masthead. No filled control lives here: the page's one primary is the
             armed card's Dispatch, and it becomes Stop while a run is live (ATTEST §5). ══ */}
      <header className="mast">
        <div className="mast-top">
        <div className="mast-main">
          <h1>{t('Agent')}</h1>
          <div className="mast-sub">
            {t('Extraction against')} <Ref>{current.code}</Ref> — {current.name}
            {current.client_name ? <> <span className="sep">·</span> {current.client_name}</> : null}
            {' '}<span className="sep">·</span> <Fig>{desk?.docs.length ?? 0}</Fig> {t('documents')}
            {' '}<span className="sep">·</span> <Fig>{desk?.dispatch?.in_scope ?? 0}</Fig> {t('in-scope requirements')}
            {desk?.dispatch?.still_draft
              ? <> <span className="sep">·</span> <Fig>{desk.dispatch.still_draft}</Fig> {t('still draft')}</>
              : null}
          </div>
        </div>
        <div className="mast-verbs ag-mast-side">
          {notice ? <div>{notice}</div> : null}
          {error && !desk?.ledger ? <div style={{ color: 'var(--broken)' }}>{error}</div> : null}
        </div>
        </div>
      </header>

      <Instrument
        health={health}
        workspace={desk?.workspace ?? null}
        live={liveRuns.length > 0}
        onRecheck={() => void refresh()}
        onRebuild={() => void rebuildWorkspace()}
        rebuilding={rebuilding}
      />

      {/* ══ Zone 1 — the desk ══ */}
      <section className="zone">
        <div className="zone-head">
          <span className="u-label">{livePass ? t('In flight') : t('Dispatch')}</span>
          <span className="zone-note zone-note--end">
            {livePass
              ? <>{t('Polled every 5 s. What you see below is what it has already')} <em>{t('filed')}</em>{t(', not what it says.')}</>
              : <>{t('Each card names the gap it closes and the refs it would touch. Everything lands in the review queue; no pass writes to the register.')}</>}
          </span>
        </div>

        {livePass ? (
          <LivePass
            run={livePass}
            progress={desk?.progress ?? null}
            now={now}
            onStop={() => void stopRun(livePass.id)}
            onNudge={(m) => nudgeRun(livePass.id, m)}
          />
        ) : (
          <>
            <ProblemState problems={problems} source="dispatch" zone={t('The dispatch deck')} onRetry={() => void refresh()} />
            {desk?.dispatch ? (
              <DispatchDeck
                plan={desk.dispatch}
                armed={armed}
                onArm={(k) => setArmed((v) => (v === k ? null : k))}
                vllmOk={vllmOk}
                liveRuns={liveRuns.length}
                note={note}
                onNote={setNote}
                onDispatch={(c) => void dispatchCard(c)}
                dispatching={dispatching}
              />
            ) : loading ? (
              <div className="ag-meta">{t('Reading the register for the gaps a pass would close…')}</div>
            ) : null}
          </>
        )}

        {/* The escape hatch lives OUTSIDE the idle/live branch on purpose: two runs may be live
            at once, and a `#agent?docs=…` arrival from Documents must land on its scope even
            when a pass is already in flight. */}
        <div>
          {pickerOpen ? (
            <ScopePicker
              docs={desk?.docs ?? []}
              selected={selected}
              setSelected={setSelected}
              instruction={instruction}
              setInstruction={setInstruction}
              onStart={() => void startFromPicker()}
              starting={starting}
              blocked={!vllmOk ? t('vLLM is unreachable — the run would die at its first turn.')
                : liveRuns.length >= 2 ? t('Two runs are already live.') : null}
              onClose={() => setPickerOpen(false)}
            />
          ) : (
            <div className="ag-escape">
              <span>{t('Something else in scope?')}</span>
              <button type="button" className="ag-link" onClick={() => setPickerOpen(true)}>
                {t('Choose documents…')}
              </button>
            </div>
          )}
        </div>
      </section>

      {/* ══ Zone 2 — the ledger ══ */}
      <section className="zone">
        <div className="zone-head">
          <span className="u-label">{t('Runs — the account each one gave of itself')}</span>
          <span className="zone-note zone-note--end">{t('Newest first. Open a run for its closing account and its transcript.')}</span>
        </div>

        {/* THE STANDING FAULT — two sentences that change how the operator writes the next
            note, and the only thing on this page that aggregates ACROSS runs. */}
        {standing && standing.pushbacks_total > 0 && standing.top_fault ? (
          <p className="ag-standing">
            <b>{t('The standing fault.')}</b> <Fig>{standing.top_fault.n}</Fig> {t('of the')}{' '}
            <Fig>{standing.pushbacks_total}</Fig> {t('tool pushbacks across every run on this engagement are one thing —')}{' '}
            <em>{standing.top_fault.text}</em>.
            {standing.review_rejections > 0 && standing.top_reject ? (
              <> {t('At review,')} <Fig>{standing.top_reject.n}</Fig> {t('of')} <Fig>{standing.review_rejections}</Fig>{' '}
                {t('rejections you made were')} <em>{standing.top_reject.reason}</em>.</>
            ) : null}
            {' '}{t('Both point at the same prompt work, not at a bad run.')}
          </p>
        ) : null}

        <ProblemState problems={problems} source="transcripts" zone={t('The fault classes')} onRetry={() => void refresh()} />
        <ProblemState problems={problems} source="ledger" zone={t('The run ledger')} onRetry={() => void refresh()} />

        {!runs.length ? (
          loading ? <div className="ag-meta">{t('Reading the ledger…')}</div> : (
            <EmptyState
              kind="empty"
              title={t('No run has been dispatched on this engagement')}
              detail={tp(desk?.docs.length ?? 0,
                'A batch pass would read the {n} linked document above and file what it finds into the review queue. Nothing it files reaches the register until you accept it.',
                'A batch pass would read the {n} linked documents above and file what it finds into the review queue. Nothing it files reaches the register until you accept it.')}
            />
          )
        ) : (
          <div className="ag-ledger">
            {runs.map((r) => {
              const open = openRun === r.id;
              return (
                <div className="ag-run" key={r.id}>
                  <button
                    type="button"
                    className="ag-run-row"
                    aria-expanded={open}
                    onClick={() => setOpenRun(open ? null : r.id)}
                  >
                    <span className="ag-run-id">
                      <span className="ag-run-ref">#{r.id}</span>
                      <span className="ag-run-when voice-figure">{stamp(r.started_at ?? r.created_at)}</span>
                    </span>
                    <span className="ag-run-mid">
                      <span className="ag-run-pass">{r.pass_label}</span>
                      <span className="ag-run-verdict">
                        {verdictOf(r, t)}
                        {' · '}
                        <span className="voice-figure">{elapsed(r.started_at, r.finished_at, now)}</span>
                      </span>
                      {r.flag ? (
                        <span className={`ag-run-flag${r.flag.tone === 'broken' ? ' ag-run-flag--bad' : ''}`}>
                          {r.flag.text}
                        </span>
                      ) : null}
                      {r.faults.length ? (
                        <span className="ag-run-push">
                          {r.faults.map((f, i) => (
                            <span key={i} style={{ display: 'block' }}>
                              <span className="n">{f.n}×</span> <Ref>{f.tool}</Ref> —{' '}
                              {f.docs.length
                                ? f.msg.split('§').map((piece, j) => (
                                  <span key={j}>{j ? <Ref>{f.docs.join(', ')}</Ref> : null}{piece}</span>
                                ))
                                : f.msg}
                            </span>
                          ))}
                        </span>
                      ) : null}
                    </span>
                    <span className="ag-run-right">
                      {r.live ? (
                        <span className="ag-seal ag-seal--none">{t('running')}</span>
                      ) : null}
                      {r.pending > 0 ? (
                        <span
                          role="button"
                          tabIndex={0}
                          className="btn btn-sm btn-accent"
                          onClick={(e) => {
                            e.stopPropagation();
                            void openRoute(ROUTES.proposals({ engagement: engagementId, run: r.id }));
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault(); e.stopPropagation();
                              void openRoute(ROUTES.proposals({ engagement: engagementId, run: r.id }));
                            }
                          }}
                        >
                          {t('Review {n} →', { n: r.pending })}
                        </span>
                      ) : null}
                      <Seal seal={r.seal} />
                    </span>
                  </button>
                  {open ? (
                    <RunExpansion
                      runId={r.id}
                      run={r}
                      onDelete={() => setConfirmDelete(r)}
                      onOpenThread={() => void openThread(r.id)}
                      onRetry={() => void refresh()}
                    />
                  ) : null}
                </div>
              );
            })}
            {/* Housekeeping lives INSIDE the sheet it acts on — it was used once, ever, and has
                no claim on the zone head (RESEARCH §3.2). */}
            {clearable > 0 ? (
              <div className="ag-ledger-foot">
                <span>
                  {tp(clearable,
                    '{n} finished run produced nothing — no proposal, no fact, no claim of either.',
                    '{n} finished runs produced nothing — no proposal, no fact, no claim of either.')}
                </span>
                <button type="button" className="ag-link" onClick={() => void cleanupRuns()}>
                  {t('Clear them')}
                </button>
              </div>
            ) : null}
          </div>
        )}
      </section>

      {/* ══ Zone 3 — ask the corpus. Collapsed until it is the zone you are in, so at rest the
             page has exactly one filled control (ATTEST §5). ══ */}
      <section className="zone" ref={dockRef}>
        <div className="zone-head">
          <span className="u-label">{t('Ask the corpus')}</span>
          <span className="zone-note zone-note--end">{t('Anything the agent records here lands in the same review queue.')}</span>
        </div>
        <div className="ag-dock">
          <button type="button" className="ag-dock-bar" onClick={() => setDockOpen((v) => !v)}>
            <span className="u-label">{dockOpen ? t('Close the thread') : t('Open the thread')}</span>
            <span className="ag-meta">
              {chatRunId
                ? <>{t('Thread')} <Ref>#{chatRunId}</Ref> {t('is open.')}</>
                : lastThread
                  ? <>
                    {t('Last thread')} <Ref>#{lastThread.id}</Ref> — {t('{proposals}, {held} held. Idle since {since}.', {
                      proposals: tp(lastThread.filed, '{n} proposal', '{n} proposals'),
                      held: lastThread.held,
                      since: stamp(lastThread.finished_at ?? lastThread.created_at),
                    })}
                  </>
                  : <>{t('No thread yet. Ask about the corpus — “which documents mention OCR?”')}</>}
            </span>
            <span className="ag-meta" style={{ marginLeft: 'auto' }}>{dockOpen ? '▾' : '▸'}</span>
          </button>

          {dockOpen ? (
            <div className="ag-dock-body">
              <div className="ag-dock-log" ref={chatLog}>
                {!chat.length ? (
                  <div className="ag-meta">
                    {t('Ask about the corpus — “what did the sponsor say about the data room?”, “which documents mention OCR?”. Anything the agent proposes lands in the review queue, never in the register.')}
                  </div>
                ) : null}
                {chat.map((entry, i) => (
                  <div key={i} className={`ag-turn ag-turn--${entry.role}`}>
                    <div className="ag-turn-who">{entry.role === 'user' ? t('You') : t('Agent')}</div>
                    {entry.text ? <div className="ag-turn-body">{entry.text}</div> : null}
                    {entry.tools?.length ? (
                      <div className="ag-reflist">
                        {entry.tools.map((tool, j) => (
                          <span key={j} className={`ag-pill${tool.pushed ? ' ag-pill--push' : ''}`}>{tool.tool}</span>
                        ))}
                      </div>
                    ) : null}
                    {entry.refs?.length ? (
                      <div className="ag-reflist">
                        {entry.refs.map((r, j) => (
                          <button
                            key={j}
                            type="button"
                            className="ag-pill att--proposed"
                            style={{ color: 'var(--act)', borderColor: 'var(--act-line)', cursor: 'pointer' }}
                            onClick={() => void openRoute(ROUTES.proposals({ engagement: engagementId, run: r.id }))}
                          >
                            {r.label}
                          </button>
                        ))}
                      </div>
                    ) : null}
                  </div>
                ))}
                {chatBusy ? (
                  <div className="ag-turn ag-turn--agent">
                    <div className="ag-turn-who">{t('Agent')}</div>
                    <div className="ag-turn-body">
                      <ChatActivity activity={chatAct} startedAt={chatStartedAt} />
                    </div>
                  </div>
                ) : null}
              </div>

              <div className="ag-row-in">
                <input
                  className="ag-input"
                  placeholder={t('Ask the corpus…')}
                  value={chatInput}
                  disabled={chatBusy || !vllmOk}
                  onChange={(e) => setChatInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); void sendChat(); } }}
                />
                {chatBusy && chatRunId ? (
                  <button type="button" className="btn btn-sm btn-danger" onClick={() => void stopChat()}>{t('Stop')}</button>
                ) : (
                  <button
                    type="button"
                    className="btn btn-sm btn-accent"
                    disabled={!chatInput.trim() || !vllmOk}
                    title={vllmOk ? undefined : t('vLLM is unreachable — the turn would die immediately.')}
                    onClick={() => void sendChat()}
                  >
                    {t('Send')}
                  </button>
                )}
                {chatRunId ? (
                  <button
                    type="button"
                    className="btn btn-sm btn-ghost"
                    title={t('Start a new thread — the current one stays in the ledger')}
                    onClick={() => { setChatRunId(null); setChat([]); }}
                  >
                    {t('New thread')}
                  </button>
                ) : null}
              </div>
            </div>
          ) : null}
        </div>
      </section>

      <ConfirmModal
        open={!!confirmDelete}
        title={t('Delete run #{id}?', { id: confirmDelete?.id })}
        confirmLabel={t('Delete')}
        danger
        onCancel={() => setConfirmDelete(null)}
        onConfirm={() => { const r = confirmDelete; setConfirmDelete(null); if (r) void deleteRun(r); }}
      >
        <p className="text-sm text-ink-2" style={{ lineHeight: 1.55 }}>
          {t('This removes the run and its transcript for good.')}
          {confirmDelete?.filed
            ? <> {t('Its')} <span className="voice-figure">{confirmDelete.filed}</span> {t('proposal(s) are kept — they keep their statement and evidence, but lose the link back to this transcript, so “why did it think that?” can no longer be answered by reading the run.')}</>
            : <> {t('It produced no proposals, so nothing else is affected.')}</>}
        </p>
        <p className="text-xs text-ink-3 mt-8">{t('The deletion itself is recorded in the audit log.')}</p>
      </ConfirmModal>
        </>
      )}
    </div>
  );
}
